import { t } from 'ttag'
import { categoryNames } from 'components/common/JobProfileWidget/text'
import { capitalizeFirstLetter } from 'utils/stringFormat'
import { getDisplayCompetencies } from 'utils/categories'

import candidate_en from 'i18n/candidate_score_card.en.json'
import candidate_nb from 'i18n/candidate_score_card.nb.json'
import recruiter_en from 'i18n/recruiter_score_card.en.json'
import recruiter_nb from 'i18n/recruiter_score_card.nb.json'

export const languages = {
  en: {
    candidate: candidate_en,
    recruiter: recruiter_en,
  },
  nb: {
    candidate: candidate_nb,
    recruiter: recruiter_nb,
  },
}

const getCopy = (language, isCandidate) =>
  languages[language][isCandidate ? 'candidate' : 'recruiter']

const JUDGEMENT_LABELS = {
  en: {
    high: 'above average',
    mid: 'average',
    low: 'below average',
  },
  nb: {
    high: 'over gjennomsnitt',
    mid: 'gjennomsnitt',
    low: 'under gjennomsnitt',
  },
}

const SCORE_DESCRIPTION_LEAD = {
  en: {
    candidate: 'Your score in {trait} is ',
    recruiter: "The candidate's score in {trait} is ",
  },
  nb: {
    candidate: 'Din skåre på {trait} er ',
    recruiter: 'Kandidatens skåre på {trait} er ',
  },
}

const NORM_COMPARISON = {
  en: ' compared to other applicants who have completed the test. ',
  nb: ' sammenlignet med andre jobbsøkere som har fullført testen. ',
}

export const scoreBucket = (score, language) => {
  if (language && JUDGEMENT_LABELS[language]) {
    const labels = JUDGEMENT_LABELS[language]

    if (score >= 0.7) return ['high', labels.high]
    if (score >= 0.3) return ['mid', labels.mid]

    return ['low', labels.low]
  }

  if (score >= 0.7) return ['high', t`above average`]
  if (score >= 0.3) return ['mid', t`average`]

  return ['low', t`below average`]
}

export const prepText = traitName => {
  const traitLabel = String(traitName ?? '')
  return value =>
    typeof value === 'string'
      ? (value ?? '??')
          .replace(/\{ *trait *\}/g, traitLabel.toLowerCase())
          .replace(/\{ *Trait *\}/g, capitalizeFirstLetter(traitLabel))
          .split(/\s*\n\s*/)
          .map(line => line.trim())
      : value
}

export const getIntro = (language = 'en', isCandidate = false) =>
  getCopy(language, isCandidate).intro

export const getPersonality = (language = 'en', isCandidate = false) =>
  getCopy(language, isCandidate).personality

export const getFactorLabel = (
  factor,
  language = 'en',
  isCandidate = false,
) => {
  const { label } = getCopy(language, isCandidate)?.traits?.[factor] ?? {}

  if (!label) return factor

  return Array.isArray(label) ? label[0] : label
}

export const getTraitTranslation = (
  trait,
  score,
  language = 'en',
  isCandidate = false,
  includeNormComparison = true,
) => {
  const {
    label = '?',
    abstract,
    lowLabel,
    highLabel,
    ...text
  } = languages[language]?.[isCandidate ? 'candidate' : 'recruiter']?.traits?.[
    trait
  ] ?? {}

  const [bucket, judgement] = scoreBucket(score, language)
  const lead =
    SCORE_DESCRIPTION_LEAD[language]?.[
      isCandidate ? 'candidate' : 'recruiter'
    ] ?? SCORE_DESCRIPTION_LEAD.en[isCandidate ? 'candidate' : 'recruiter']
  const normComparison = NORM_COMPARISON[language] ?? NORM_COMPARISON.en

  const description =
    lead +
    judgement +
    (includeNormComparison ? normComparison : '. ') +
    (text[bucket] || '')

  return Object.fromEntries(
    Object.entries({
      label,
      score,
      abstract,
      lowLabel,
      highLabel,
      description,
      judgement,
    }).map(([key, value]) => [key, prepText(label)(value)]),
  )
}

export const getTraitsTranslations = (
  traits,
  response,
  language = 'en',
  isCandidate = false,
) => {
  if (!traits || traits.length === 0) {
    return []
  }

  const traitsDefs = getCopy(language, isCandidate)['traits']

  return traits.map(trait => ({
    ...traitsDefs[trait],
    scores: response?.map(({ id, scores }) => ({
      id: id,
      score: Math.round(scores[trait] * 100),
    })),
    id: trait,
  }))
}

export const getCategoryKeys = categories => {
  return Object.keys(categories).filter(key => categories[key])
}

/**
 * The competencies the report shows for a project.
 *
 * A project can list the competencies its report should present, which is
 * independent of the competencies selected for scoring: a score is calculated
 * for every competency regardless of the selection, so any of them can be
 * shown. Without such a list the report falls back to the selected ones.
 */
export const getReportCategoryKeys = profile =>
  getDisplayCompetencies(profile) ?? getCategoryKeys(profile?.categories ?? {})

export const getVisibleCategory = (categoryKeys, scores, language) => {
  const categoriesDefs = categoryNames(language)

  return categoryKeys.map(key => ({
    score: Math.max(Math.min(Math.floor(scores[key] * 5), 4), 1),
    label: categoriesDefs[key][0],
  }))
}

export const getVisibleCategories = (categoryKeys, scores, language) => {
  const categoriesDefs = categoryNames(language)

  return scores.map(scores =>
    categoryKeys.map(key => ({
      score: Math.max(Math.min(Math.floor(scores[key] * 5), 4), 1),
      label: categoriesDefs[key][0],
    })),
  )
}
